<template>
  <form
    ref="form"
    @submit.prevent="handleSubmit"
  >
    <div :class="props.formClass">
      <slot />
    </div>
  </form>
</template>

<script lang="ts">
export default {
  name: "BaseFormWrapper",
};
</script>

<script setup lang="ts">
import { isRef, provide, type Ref, ref } from "vue";

import { FormWrapperSymbol } from "shared/components/base/symbols";

export interface Props {
  formClass?: string | string[] | Record<string, boolean>;
}

export interface FormInput {
  clearValidations: () => void;
  dirty: boolean | Ref<boolean>;
  focus?: () => void;
  reset: () => void;
  validate: () => Promise<boolean>;
}

export interface FormWrapperProvide {
  register: (input: FormInput) => void;
  unregister: (input: FormInput) => void;
}

interface IsValidOptions {
  showAllErrors?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  formClass: "",
});

const emit = defineEmits<{
  submit: [Event];
}>();

const form = ref<HTMLFormElement>();
const registeredInputs: FormInput[] = [];

const noop = () => {};

const isValid = async ({
  showAllErrors,
}: IsValidOptions = {}): Promise<boolean> => {
  let result = true;

  for (let index = 0; index < registeredInputs.length; index += 1) {
    const instance = registeredInputs[index];

    if (isRef(instance.dirty)) {
      instance.dirty.value = true;
    } else {
      instance.dirty = true;
    }

    // eslint-disable-next-line no-await-in-loop
    const validateResult = await instance.validate();

    // focus first invalid input and stop validation
    if (!validateResult) {
      (instance.focus || noop)();
      result = false;
      if (!showAllErrors) break;
    }
  }

  return result;
};

const handleSubmit = async (event: Event): Promise<void> => {
  const result = await isValid();

  if (result) {
    emit("submit", event);
  }
};

const reset = (): void => {
  Array.from(registeredInputs).forEach((input) => {
    input.reset();
  });
};

const clearValidations = (): void => {
  Array.from(registeredInputs).forEach((input) => {
    input.clearValidations();
  });
};

const register: FormWrapperProvide["register"] = (input) => {
  registeredInputs.push(input);
};

const unregister: FormWrapperProvide["unregister"] = (input) => {
  registeredInputs.splice(registeredInputs.indexOf(input), 1);
};

provide(FormWrapperSymbol, {
  register,
  unregister,
});

defineExpose({
  registeredInputs,
  register,
  unregister,
  isValid,
  reset,
  handleSubmit,
  clearValidations,
});
</script>

<style lang="scss" scoped>
form {
  position: relative;
}
</style>
